import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { useFlashToast } from '../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUser, faEnvelope, faIdCard, faLocationDot,
  faLock, faEye, faEyeSlash, faFloppyDisk, faCommentDots, faShield
} from '@fortawesome/free-solid-svg-icons'
import UserSidebar from '../../components/user/Sidebar'
import UserTopbar from '../../components/user/Topbar'

function InputField({ label, icon, readOnly = false, hint, ...props }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <div className="relative">
        <FontAwesomeIcon icon={icon} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          readOnly={readOnly}
          className={`w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border outline-none transition-all ${
            readOnly
              ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white border-gray-300 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-500/20'
          }`}
          {...props}
        />
      </div>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

function PasswordField({ label, icon: _icon, showToggle: _showToggle, ...props }) {
  const [show, setShow] = useState(false)
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <div className="relative">
        <FontAwesomeIcon icon={faLock} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type={show ? 'text' : 'password'}
          className="w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border border-gray-300 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition-all"
          {...props}
        />
        <button type="button" onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={show ? faEyeSlash : faEye} className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

export default function UserSettings({ current_user }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const profile = useForm({
    update_type: 'profile',
    name: current_user?.name || '',
    address: current_user?.address || '',
    whatsapp_number: current_user?.whatsapp_number || '',
  })

  const password = useForm({
    update_type: 'password',
    current_password: '',
    password: '',
    password_confirmation: '',
  })

  const submitProfile = (e) => {
    e.preventDefault()
    profile.patch('/user/settings')
  }

  const submitPassword = (e) => {
    e.preventDefault()
    password.patch('/user/settings', {
      onSuccess: () => password.reset(),
    })
  }

  const initials = (current_user?.name || current_user?.username || '?')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <UserSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <UserTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          {/* Page header */}
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-900">Pengaturan Akun</h1>
            <p className="text-sm text-gray-500 mt-0.5">Kelola informasi profil dan keamanan akun Anda</p>
          </div>

          <div className="max-w-4xl mx-auto w-full space-y-6">

            {/* ── Identity card (read-only overview) ── */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Green accent bar */}
              <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-400" />
              <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                {/* Avatar initials */}
                <div className="w-16 h-16 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-green-200">
                  <span className="text-white text-xl font-bold">{initials}</span>
                </div>

                {/* Info grid */}
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                  <div className="col-span-2 sm:col-span-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">Nama Lengkap</p>
                    <p className="text-sm font-semibold text-gray-900">{current_user?.name || <span className="text-gray-400 italic font-normal">Belum diisi</span>}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">Username</p>
                    <p className="text-sm text-gray-700 font-mono">@{current_user?.username}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">Tipe Akun</p>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700 ring-1 ring-green-200">
                      <FontAwesomeIcon icon={faShield} className="w-2.5 h-2.5" />
                      {current_user?.user_type === 'external' ? 'Eksternal' : 'Internal'}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">Email</p>
                    <p className="text-sm text-gray-700 truncate">{current_user?.email}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">NIK</p>
                    <div className="flex items-center gap-1.5">
                      <FontAwesomeIcon icon={faIdCard} className="w-3 h-3 text-gray-400" />
                      <p className="text-sm font-mono tracking-widest text-gray-800">
                        {current_user?.nik
                          ? current_user.nik.replace(/(.{4})(.{4})(.{4})(.*)/, '$1 $2 $3 $4')
                          : <span className="text-gray-400 italic font-sans text-xs tracking-normal">Belum diisi</span>}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="px-6 pb-4 text-[11px] text-gray-400">
                Username, email, dan NIK tidak dapat diubah. Hubungi administrator jika ada kesalahan data.
              </p>
            </div>

            {/* ── 2-column edit section ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Edit Data Diri */}
              <div className="bg-white rounded-xl border border-gray-200 flex flex-col">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                    <FontAwesomeIcon icon={faUser} className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-800">Edit Data Diri</h2>
                    <p className="text-[11px] text-gray-400">Perbarui informasi kontak Anda</p>
                  </div>
                </div>
                <form onSubmit={submitProfile} className="p-6 flex flex-col gap-4 flex-1">
                  <InputField
                    label="Nama Lengkap"
                    icon={faUser}
                    value={profile.data.name}
                    onChange={e => profile.setData('name', e.target.value)}
                    placeholder="Nama lengkap Anda"
                  />
                  <InputField
                    label="Nomor WhatsApp"
                    icon={faCommentDots}
                    value={profile.data.whatsapp_number}
                    onChange={e => profile.setData('whatsapp_number', e.target.value)}
                    placeholder="Contoh: 08123456789"
                  />
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Alamat</label>
                    <div className="relative">
                      <FontAwesomeIcon icon={faLocationDot} className="absolute left-3 top-3 w-3.5 h-3.5 text-gray-400" />
                      <textarea
                        rows={4}
                        value={profile.data.address}
                        onChange={e => profile.setData('address', e.target.value)}
                        placeholder="Alamat lengkap Anda"
                        className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition-all resize-none"
                      />
                    </div>
                  </div>
                  <div className="mt-auto pt-2">
                    <button
                      type="submit"
                      disabled={profile.processing}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <FontAwesomeIcon icon={faFloppyDisk} className="w-3.5 h-3.5" />
                      {profile.processing ? 'Menyimpan...' : 'Simpan Perubahan'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Ubah Password */}
              <div className="bg-white rounded-xl border border-gray-200 flex flex-col">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <FontAwesomeIcon icon={faLock} className="w-3.5 h-3.5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-800">Ubah Password</h2>
                    <p className="text-[11px] text-gray-400">Gunakan password yang kuat dan unik</p>
                  </div>
                </div>
                <form onSubmit={submitPassword} className="p-6 flex flex-col gap-4 flex-1">
                  {/* Hidden username for password-manager accessibility */}
                  <input type="text" name="username" autoComplete="username" value={current_user?.email || ''} readOnly className="hidden" aria-hidden="true" />
                  <PasswordField
                    label="Password Saat Ini"
                    value={password.data.current_password}
                    onChange={e => password.setData('current_password', e.target.value)}
                    placeholder="Masukkan password saat ini"
                    autoComplete="current-password"
                  />
                  <PasswordField
                    label="Password Baru"
                    value={password.data.password}
                    onChange={e => password.setData('password', e.target.value)}
                    placeholder="Minimal 6 karakter"
                    autoComplete="new-password"
                  />
                  <PasswordField
                    label="Konfirmasi Password Baru"
                    value={password.data.password_confirmation}
                    onChange={e => password.setData('password_confirmation', e.target.value)}
                    placeholder="Ulangi password baru"
                    autoComplete="new-password"
                  />
                  <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3">
                    <p className="text-[11px] text-amber-700 leading-relaxed">
                      Password baru minimal 6 karakter dan tidak boleh sama dengan password lama.
                    </p>
                  </div>
                  <div className="mt-auto pt-2">
                    <button
                      type="submit"
                      disabled={password.processing}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <FontAwesomeIcon icon={faLock} className="w-3.5 h-3.5" />
                      {password.processing ? 'Memproses...' : 'Perbarui Password'}
                    </button>
                  </div>
                </form>
              </div>

            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
